/**
 * BrowserAgent - AI that browses the web for you
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Globe, Camera, FileInput, Search, DollarSign,
  Loader2, CheckCircle, Download, ArrowRight, Zap
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMPLATES = [
  {
    id: 'web-scraper',
    name: 'Web Scraper',
    description: 'Extract data from any website',
    icon: Globe,
    color: 'teal'
  },
  {
    id: 'screenshot-capture',
    name: 'Screenshot Capture',
    description: 'Take screenshots of any webpage',
    icon: Camera,
    color: 'blue'
  },
  {
    id: 'competitor-research',
    name: 'Competitor Research',
    description: 'Analyze competitor websites',
    icon: Search,
    color: 'purple'
  },
  {
    id: 'price-monitor',
    name: 'Price Monitor',
    description: 'Monitor prices on e-commerce sites',
    icon: DollarSign,
    color: 'green'
  }
];

export default function BrowserAgent() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Form state
  const [url, setUrl] = useState('');
  const [dataToExtract, setDataToExtract] = useState('');
  const [outputFormat, setOutputFormat] = useState('json');

  useEffect(() => {
    if (authLoading) return; // Wait for auth to initialize
    if (!user || !token) {
      navigate('/auth?redirect=/browser-agent');
    }
  }, [user, token, authLoading]);

  const handleScrape = async () => {
    if (!url) {
      toast.error('Please enter a URL');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await axios.post(`${API}/browser-agent/scrape`, {
        url,
        data_to_extract: dataToExtract || 'Extract all main content and data',
        output_format: outputFormat
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setResult(response.data);
      toast.success('Data extracted successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scraping failed');
    } finally {
      setLoading(false);
    }
  };

  const handleScreenshot = async () => {
    if (!url) {
      toast.error('Please enter a URL');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await axios.post(`${API}/browser-agent/screenshot?url=${encodeURIComponent(url)}&full_page=true&wait_seconds=3`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setResult(response.data);
      toast.success('Screenshot captured!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Screenshot failed');
    } finally {
      setLoading(false);
    }
  };

  const downloadScreenshot = () => {
    if (result?.screenshot_base64) {
      const link = document.createElement('a');
      link.href = `data:image/png;base64,${result.screenshot_base64}`;
      link.download = `screenshot-${new Date().toISOString().split('T')[0]}.png`;
      link.click();
    }
  };

  const colorMap = {
    teal: 'from-teal-500/20 to-teal-500/10 border-teal-500/30 text-teal-400',
    blue: 'from-blue-500/20 to-blue-500/10 border-blue-500/30 text-blue-400',
    purple: 'from-purple-500/20 to-purple-500/10 border-purple-500/30 text-purple-400',
    green: 'from-green-500/20 to-green-500/10 border-green-500/30 text-green-400'
  };

  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-16 h-16 bg-gradient-to-br from-teal-500/20 to-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Globe className="w-8 h-8 text-teal-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Browser Agent</h1>
          <p className="text-zinc-400 max-w-xl mx-auto">
            AI that browses the web for you. Scrape data, take screenshots, 
            and automate web tasks without writing code.
          </p>
        </motion.div>

        {/* Template Selection */}
        {!selectedTemplate && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h2 className="text-xl font-bold text-white mb-6">What do you want to do?</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {TEMPLATES.map((template, index) => {
                const Icon = template.icon;
                return (
                  <motion.div
                    key={template.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + index * 0.05 }}
                    onClick={() => setSelectedTemplate(template)}
                    className={`bg-gradient-to-br ${colorMap[template.color]} border rounded-xl p-6 cursor-pointer hover:scale-[1.02] transition-transform`}
                  >
                    <Icon className="w-8 h-8 mb-3" />
                    <h3 className="text-lg font-semibold text-white mb-1">{template.name}</h3>
                    <p className="text-zinc-400 text-sm">{template.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Task Form */}
        {selectedTemplate && !result && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-zinc-900/50 border border-white/10 rounded-xl p-6"
          >
            <button
              onClick={() => setSelectedTemplate(null)}
              className="text-zinc-400 hover:text-white mb-4 text-sm"
            >
              ← Back to templates
            </button>

            <div className="flex items-center gap-3 mb-6">
              <selectedTemplate.icon className={`w-6 h-6 ${colorMap[selectedTemplate.color].split(' ').pop()}`} />
              <h2 className="text-xl font-bold text-white">{selectedTemplate.name}</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-zinc-400 text-sm mb-2 block">Website URL</label>
                <Input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com"
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>

              {(selectedTemplate.id === 'web-scraper' || selectedTemplate.id === 'competitor-research') && (
                <>
                  <div>
                    <label className="text-zinc-400 text-sm mb-2 block">What data do you need?</label>
                    <Textarea
                      value={dataToExtract}
                      onChange={(e) => setDataToExtract(e.target.value)}
                      placeholder="e.g., All product names and prices, contact information, article titles..."
                      className="bg-zinc-800 border-zinc-700 text-white min-h-[100px]"
                    />
                  </div>

                  <div>
                    <label className="text-zinc-400 text-sm mb-2 block">Output Format</label>
                    <select
                      value={outputFormat}
                      onChange={(e) => setOutputFormat(e.target.value)}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                    >
                      <option value="json">JSON</option>
                      <option value="csv">CSV</option>
                      <option value="text">Plain Text</option>
                    </select>
                  </div>
                </>
              )}

              <Button
                onClick={selectedTemplate.id === 'screenshot-capture' ? handleScreenshot : handleScrape}
                disabled={loading || !url}
                className="w-full bg-gradient-to-r from-teal-500 to-blue-500 h-12"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Zap className="w-5 h-5 mr-2" />
                    {selectedTemplate.id === 'screenshot-capture' ? 'Capture Screenshot' : 'Extract Data'}
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        )}

        {/* Results */}
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-zinc-900/50 border border-green-500/30 rounded-xl p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <CheckCircle className="w-6 h-6 text-green-400" />
              <h2 className="text-xl font-bold text-white">Task Complete!</h2>
            </div>

            {result.screenshot_base64 && (
              <div className="mb-6">
                <img
                  src={`data:image/png;base64,${result.screenshot_base64}`}
                  alt="Screenshot"
                  className="w-full rounded-lg border border-zinc-700"
                />
                <Button
                  onClick={downloadScreenshot}
                  className="mt-4 bg-teal-500 hover:bg-teal-600"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Screenshot
                </Button>
              </div>
            )}

            {result.extracted_data && (
              <div className="mb-6">
                <h3 className="text-white font-semibold mb-2">Extracted Data:</h3>
                <pre className="bg-zinc-800 rounded-lg p-4 overflow-x-auto text-sm text-zinc-300 max-h-[400px] overflow-y-auto">
                  {typeof result.extracted_data === 'string' 
                    ? result.extracted_data 
                    : JSON.stringify(result.extracted_data, null, 2)}
                </pre>
              </div>
            )}

            <div className="flex gap-4">
              <Button
                onClick={() => {
                  setResult(null);
                  setUrl('');
                  setDataToExtract('');
                }}
                variant="outline"
                className="border-zinc-700 text-zinc-300"
              >
                New Task
              </Button>
              <Button
                onClick={() => {
                  setResult(null);
                  setSelectedTemplate(null);
                }}
                variant="ghost"
                className="text-zinc-400"
              >
                Choose Different Template
              </Button>
            </div>
          </motion.div>
        )}

        {/* Features Callout */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid md:grid-cols-3 gap-4 mt-12"
        >
          <div className="bg-zinc-900/30 rounded-xl p-4 text-center">
            <Globe className="w-8 h-8 text-teal-400 mx-auto mb-2" />
            <p className="text-white font-semibold">Any Website</p>
            <p className="text-zinc-500 text-sm">Works with any public URL</p>
          </div>
          <div className="bg-zinc-900/30 rounded-xl p-4 text-center">
            <Zap className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
            <p className="text-white font-semibold">AI-Powered</p>
            <p className="text-zinc-500 text-sm">Smart data extraction</p>
          </div>
          <div className="bg-zinc-900/30 rounded-xl p-4 text-center">
            <FileInput className="w-8 h-8 text-blue-400 mx-auto mb-2" />
            <p className="text-white font-semibold">Multiple Formats</p>
            <p className="text-zinc-500 text-sm">JSON, CSV, or plain text</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
